import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GripVertical, Pin, PinOff, Scan, X } from 'lucide-react';

import type { TtqSession } from '../../services/ttqService';
import { TtqNumberNode } from './TtqNumberNode';
import { buildParticleCardFromEntry, buildParticleCards, TtqParticleCard } from './TtqParticleCard';
import {
  listTtqBirdsEyeConvergeMemberIds,
} from './ttqibBirdsEyeLayout';
import {
  listTtqConvergeLodEdges,
  placeTtqBirdsEyeNumberNodes,
  placeTtqConvergeLodAnchors,
  placeTtqNumberNodeAnchors,
  TTQ_CONVERGE_CARD_H,
  TTQ_CONVERGE_CARD_W,
  ttqNumberNodeLodBoost,
  ttqRayAabbExitPoint,
} from './ttqibConvergeLodLayout';
import { collectCttqConvergeParticleIds } from './ttqibParticleNumbers';
import { computeFocusCardViewportInLod } from './ttqibFocusViewport';
import { estimateWholeHeight, TTQIB_CARD_WIDTH } from './ttqibObjectLayout';
import {
  computeDefaultColumnViewport,
  computeFitViewport,
  normalizeViewport,
  ttqConvergeLodScreenBoost,
  TTQIB_ZOOM_MAX,
  TTQIB_ZOOM_MIN,
  TTQ_SESSION_OVERLAY_TOP_CLASS,
} from './ttqibViewport';
import { useTtqCanvasMultiTouchPan } from './useTtqCanvasMultiTouchPan';
import {
  isQuestionChainDerivedParticle,
  resolveDerivedProvenance,
  type TtqDerivedProvenanceView,
} from './ttqibDerivedStack';
import type { TtqibNativeCanvasModel, TtqibViewport } from './types';

type LodFloatState = {
  particleId: string;
  pinned: boolean;
  left: number;
  top: number;
  width: number;
  height: number;
  /** 源标号节点中心（风筝线起点） */
  nodeCx: number;
  nodeCy: number;
};

type FloatDragState = {
  particleId: string;
  startClientX: number;
  startClientY: number;
  originLeft: number;
  originTop: number;
};

/** hover 浮窗与收敛卡同宽同风格（非一对多整卡 preview） */
const FLOAT_CARD_WIDTH = TTQ_CONVERGE_CARD_W;
const FLOAT_DEFAULT_H = TTQ_CONVERGE_CARD_H;

type TtqNativeCanvasProps = {
  session?: TtqSession | null;
  canvasModel: TtqibNativeCanvasModel | null;
  /** 双状态 LOD：true = 多对一（衍生标号 + 收敛摘要）；false = 一对多（整卡 + 收敛标号） */
  birdsEyeMode?: boolean;
  viewport?: TtqibViewport;
  viewportInitialized?: boolean;
  onViewportChange?: (viewport: TtqibViewport) => void;
  /** @deprecated 节点不再可拖；保留以免旧调用报错 */
  onMoveParticle?: (particleId: string, position: { x: number; y: number }) => void;
  onSwitchLayer?: (layerId: string) => void;
  onSelectObject?: (objectId: string | null) => void;
  selectedObjectIds?: string[];
  selectedSegmentObjectIds?: string[];
  onBehaviorSelectionChange?: (particleIds: string[]) => void;
  onToggleSegment?: (item: { objectId: string; text: string; particleId: string }) => void;
  onOpenPinTi?: (particleId: string) => void;
  onOpenLadderPractice?: (particleId: string) => void;
  showLadderPractice?: boolean;
  onClearSegmentTray?: () => void;
  tastedAnchorsByParticleId?: Record<string, string[]>;
  chatAnchorsByParticleId?: Record<string, import('../../utils/aiChatSegmentKey').AiChatSegmentAnchor[]>;
  onOpenChatThread?: (threadId: string) => void;
  derivedProvenanceByParticleId?: Record<string, TtqDerivedProvenanceView>;
  derivedParticleIds?: string[];
  onDeleteDerivedParticle?: (particleId: string) => void;
  onRegenerateDerivedParticle?: (particleId: string) => void;
  regeneratingDerivedId?: string | null;
  onMeasureParticle?: (particleId: string, size: { width: number; height: number }) => void;
  linkedRefsByParticleId?: Record<string, Array<{ particleId: string; title: string }>>;
  onFocusLinkedParticle?: (particleId: string) => void;
  /** @deprecated Scan 改用本地容器尺寸计算放大居中 */
  onCenterParticle?: (particleId: string) => void;
  onEnterBirdsEyeZoom?: () => void;
  /** 钉住浮窗：写入 ttqibState.birdsEyePinnedFloats（localStorage + 远端） */
  pinnedFloats?: Array<{ particleId: string; left: number; top: number }>;
  onPinnedFloatsChange?: (floats: Array<{ particleId: string; left: number; top: number }>) => void;
  birdsSelectedIds?: string[];
  onBirdsSelectedChange?: (ids: string[]) => void;
  cttqAggregating?: boolean;
  cttqMemberPickTargetId?: string | null;
  onStartCttqMemberPick?: (convergeParticleId: string) => void;
  onCancelCttqMemberPick?: () => void;
  onPickCttqMember?: (convergeParticleId: string, memberParticleId: string) => void;
  focusParticleId?: string | null;
  onFocusParticleHandled?: () => void;
  /** 递增时清除收敛卡位置锁，强制 placeTtqConvergeLodAnchors 重算 */
  convergeRelayoutNonce?: number;
};

type PanState = {
  startClientX: number;
  startClientY: number;
  originX: number;
  originY: number;
  moved: boolean;
  fromCard: boolean;
};

const DRAG_THRESHOLD_PX = 3;

export function TtqNativeCanvas({
  session = null,
  canvasModel,
  birdsEyeMode = false,
  viewport,
  viewportInitialized,
  onViewportChange,
  onSelectObject,
  selectedObjectIds = [],
  selectedSegmentObjectIds = [],
  onBehaviorSelectionChange,
  onToggleSegment,
  onOpenPinTi,
  onOpenLadderPractice,
  showLadderPractice = false,
  onClearSegmentTray,
  tastedAnchorsByParticleId = {},
  chatAnchorsByParticleId = {},
  onOpenChatThread,
  derivedProvenanceByParticleId = {},
  derivedParticleIds = [],
  onDeleteDerivedParticle,
  onRegenerateDerivedParticle,
  regeneratingDerivedId = null,
  onMeasureParticle,
  linkedRefsByParticleId = {},
  onFocusLinkedParticle,
  onEnterBirdsEyeZoom,
  pinnedFloats = [],
  onPinnedFloatsChange,
  birdsSelectedIds = [],
  onBirdsSelectedChange,
  cttqAggregating = false,
  cttqMemberPickTargetId = null,
  onStartCttqMemberPick,
  onCancelCttqMemberPick,
  onPickCttqMember,
  focusParticleId = null,
  onFocusParticleHandled,
  convergeRelayoutNonce = 0,
}: TtqNativeCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panRef = useRef<PanState | null>(null);
  const vpRef = useRef(normalizeViewport(viewport ?? canvasModel?.state.viewport));
  const floatElsRef = useRef<Record<string, HTMLDivElement | null>>({});
  const hoverTimer = useRef<number | null>(null);
  const floatDragRef = useRef<FloatDragState | null>(null);
  const readyToPersistPinsRef = useRef(false);
  const pinnedSnapshotRef = useRef('');
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(selectedObjectIds[0] ?? null);
  const [floats, setFloats] = useState<LodFloatState[]>([]);

  const vp = normalizeViewport(viewport ?? canvasModel?.state.viewport);
  vpRef.current = vp;

  useEffect(() => {
    setLocalSelectedId(selectedObjectIds[0] ?? null);
  }, [selectedObjectIds]);

  useEffect(() => {
    readyToPersistPinsRef.current = false;
  }, [session?.id]);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimer.current != null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  useEffect(() => {
    if (!birdsEyeMode) {
      // 离开关系层：只收起未钉浮窗；钉住的保留，回来即显
      setFloats((current) => current.filter((item) => item.pinned));
      clearHoverTimer();
    }
  }, [birdsEyeMode, clearHoverTimer]);

  const cards = useMemo(
    () => (canvasModel ? buildParticleCards(canvasModel.objects) : []),
    [canvasModel],
  );
  const derivedIdSet = useMemo(() => new Set(derivedParticleIds), [derivedParticleIds]);
  const displayNumbers = canvasModel?.state.particleNumbers?.byId ?? {};
  const birdsSelectedSet = useMemo(() => new Set(birdsSelectedIds), [birdsSelectedIds]);

  const cardRectById = useMemo(() => {
    const map = new Map<string, { x: number; y: number; width: number; height: number }>();
    for (const card of cards) {
      const width = card.width || card.whole.width || TTQIB_CARD_WIDTH;
      const height = Math.max(card.whole.height ?? 0, estimateWholeHeight());
      map.set(card.particleId, { x: card.x, y: card.y, width, height });
    }
    return map;
  }, [cards]);

  const [convergeMeasuredH, setConvergeMeasuredH] = useState<Record<string, number>>({});

  /** 关系层：抵消 viewport.zoom，让收敛卡屏幕尺寸接近可读整卡 */
  const convergeScreenBoost = birdsEyeMode ? ttqConvergeLodScreenBoost(vp.zoom) : 1;
  const numberNodeBoost = birdsEyeMode ? ttqNumberNodeLodBoost(vp.zoom) : 1;

  const convergeIdSet = useMemo(
    () => (session ? collectCttqConvergeParticleIds(session) : new Set<string>()),
    [session],
  );

  const numberNodePlacements = useMemo(() => {
    if (!birdsEyeMode) return [] as ReturnType<typeof placeTtqNumberNodeAnchors>;
    if (session) {
      const grid = placeTtqBirdsEyeNumberNodes(session, numberNodeBoost);
      if (grid.length > 0) return grid;
    }
    const ids = cards
      .map((card) => card.particleId)
      .filter((id) => !convergeIdSet.has(id));
    return placeTtqNumberNodeAnchors(cardRectById, ids, numberNodeBoost);
  }, [birdsEyeMode, cardRectById, cards, convergeIdSet, numberNodeBoost, session]);

  const numberNodePlacementById = useMemo(() => {
    const map = new Map<string, (typeof numberNodePlacements)[number]>();
    for (const item of numberNodePlacements) {
      map.set(item.particleId, item);
    }
    return map;
  }, [numberNodePlacements]);

  const numberNodeObstacles = useMemo(
    () => numberNodePlacements.map((item) => ({
      x: item.x,
      y: item.y,
      width: item.width,
      height: item.height,
    })),
    [numberNodePlacements],
  );

  /** 结构指纹：成员增删 / 小卡网格变化时重算收敛落位；zoom/boost 不挪 x/y */
  const convergeStructureKey = useMemo(() => {
    if (!session) return '';
    const ids = [...convergeIdSet].sort();
    const parts = ids.map((particleId) => {
      const memberIds = listTtqBirdsEyeConvergeMemberIds(session, particleId);
      const anchors = memberIds.map((mid) => {
        const placed = numberNodePlacementById.get(mid);
        if (placed) {
          return `${mid}:${Math.round(placed.x)}:${Math.round(placed.y)}:${Math.round(placed.width)}:${Math.round(placed.height)}`;
        }
        const rect = cardRectById.get(mid);
        if (!rect) return mid;
        return `${mid}:${Math.round(rect.x)}:${Math.round(rect.y)}:${Math.round(rect.width)}:${Math.round(rect.height)}`;
      });
      return `${particleId}=${memberIds.slice().sort().join('+')}|${anchors.join(',')}`;
    });
    return `${session.id}|b${birdsEyeMode ? 1 : 0}|${parts.join(';')}`;
  }, [birdsEyeMode, cardRectById, convergeIdSet, numberNodePlacementById, session]);

  /** 测高指纹：展开参考答案等高度变化时必须解除位置锁，重新跑避让 */
  const convergeHeightKey = useMemo(
    () => Object.keys(convergeMeasuredH)
      .sort()
      .map((id) => `${id}:${Math.round(convergeMeasuredH[id] || 0)}`)
      .join('|'),
    [convergeMeasuredH],
  );

  const convergePosLockRef = useRef<{
    key: string;
    pos: Map<string, { x: number; y: number }>;
  }>({ key: '', pos: new Map() });

  const convergePlacementsRaw = useMemo(
    () => (session
      ? placeTtqConvergeLodAnchors(session, cardRectById, {
        screenBoost: convergeScreenBoost,
        measuredHeights: convergeMeasuredH,
        numberNodeObstacles,
        numberNodePlacements,
        numberNodeBoost,
      })
      : []),
    [
      cardRectById,
      convergeMeasuredH,
      convergeScreenBoost,
      numberNodeBoost,
      numberNodeObstacles,
      numberNodePlacements,
      session,
    ],
  );

  const convergePlacements = useMemo(() => {
    const lock = convergePosLockRef.current;
    const effectiveKey = `${convergeStructureKey}#${convergeRelayoutNonce}#h:${convergeHeightKey}`;
    if (lock.key !== effectiveKey) {
      lock.key = effectiveKey;
      lock.pos = new Map();
    }
    return convergePlacementsRaw.map((item) => {
      const locked = lock.pos.get(item.particleId);
      if (locked) {
        return { ...item, x: locked.x, y: locked.y };
      }
      lock.pos.set(item.particleId, { x: item.x, y: item.y });
      return item;
    });
  }, [convergeHeightKey, convergePlacementsRaw, convergeRelayoutNonce, convergeStructureKey]);

  const convergeBoardCards = useMemo(() => {
    const map = new Map<string, ReturnType<typeof buildParticleCardFromEntry>>();
    for (const item of convergePlacements) {
      // 坐标交给外层 wrapper（便于 scale 补偿）；卡内用 0,0
      map.set(
        item.particleId,
        buildParticleCardFromEntry(item.entry, {
          x: 0,
          y: 0,
          width: item.width,
          layerId: 'lod-converge',
        }),
      );
    }
    return map;
  }, [convergePlacements]);

  const convergeRectById = useMemo(() => {
    const map = new Map<string, { x: number; y: number; width: number; height: number }>();
    for (const item of convergePlacements) {
      const baseH = convergeMeasuredH[item.particleId] ?? item.height ?? TTQ_CONVERGE_CARD_H;
      map.set(item.particleId, {
        x: item.x,
        y: item.y,
        width: item.width * convergeScreenBoost,
        height: baseH * convergeScreenBoost,
      });
    }
    return map;
  }, [convergeMeasuredH, convergePlacements, convergeScreenBoost]);

  const resolveRect = useCallback((particleId: string) => {
    const placed = numberNodePlacementById.get(particleId);
    if (placed) {
      return {
        x: placed.x,
        y: placed.y,
        width: placed.width,
        height: placed.height,
      };
    }
    return cardRectById.get(particleId) ?? convergeRectById.get(particleId) ?? null;
  }, [cardRectById, convergeRectById, numberNodePlacementById]);

  /** 一对多：源→衍生 / 关联 */
  const deriveEdges = useMemo(() => {
    const present = new Set(cards.map((card) => card.particleId));
    const edges: Array<{ id: string; fromId: string; toId: string; kind: 'derive' }> = [];
    const seen = new Set<string>();
    const push = (fromId: string, toId: string, id: string) => {
      const from = String(fromId || '').trim();
      const to = String(toId || '').trim();
      if (!from || !to || from === to || !present.has(from) || !present.has(to)) return;
      const key = `${from}->${to}`;
      if (seen.has(key)) return;
      seen.add(key);
      edges.push({ id, fromId: from, toId: to, kind: 'derive' });
    };

    for (const [toId, provenance] of Object.entries(derivedProvenanceByParticleId)) {
      if (convergeRectById.has(toId)) continue;
      const members = provenance.memberParticleIds?.filter(Boolean) ?? [];
      if (members.length >= 2) {
        for (const fromId of members) {
          push(fromId, toId, `der:${provenance.pathRunId}:${fromId}:${toId}`);
        }
      } else if (provenance.sourceParticleId) {
        push(provenance.sourceParticleId, toId, `der:${provenance.pathRunId}:${toId}`);
      }
    }

    for (const assoc of canvasModel?.state.associations ?? []) {
      push(
        assoc.sourceParticleId,
        assoc.linkedParticleId,
        `assoc:${assoc.sourceParticleId}:${assoc.linkedParticleId}`,
      );
    }

    return edges;
  }, [cards, canvasModel?.state.associations, convergeRectById, derivedProvenanceByParticleId]);

  const convergeEdges = useMemo(
    () => (session ? listTtqConvergeLodEdges(session) : []),
    [session],
  );

  const worldSize = useMemo(() => {
    let maxX = 400;
    let maxY = 400;
    for (const rect of cardRectById.values()) {
      maxX = Math.max(maxX, rect.x + rect.width + 80);
      maxY = Math.max(maxY, rect.y + rect.height + 80);
    }
    for (const rect of convergeRectById.values()) {
      maxX = Math.max(maxX, rect.x + rect.width + 80);
      maxY = Math.max(maxY, rect.y + rect.height + 80);
    }
    for (const floatItem of floats) {
      const fw = (floatItem.width || FLOAT_CARD_WIDTH) * convergeScreenBoost;
      const fh = (floatItem.height || FLOAT_DEFAULT_H) * convergeScreenBoost;
      maxX = Math.max(maxX, floatItem.left + fw + 80);
      maxY = Math.max(maxY, floatItem.top + fh + 80);
    }
    return { width: maxX, height: maxY };
  }, [cardRectById, convergeRectById, convergeScreenBoost, floats]);

  const selectedParticleIdSet = useMemo(() => {
    if (birdsEyeMode) return birdsSelectedSet;
    const ids = new Set<string>();
    const objectIds = new Set(
      [localSelectedId, ...selectedObjectIds].filter((id): id is string => Boolean(id)),
    );
    if (objectIds.size === 0 || !canvasModel) return ids;
    for (const object of canvasModel.objects) {
      if (objectIds.has(object.id)) ids.add(object.particleId);
    }
    return ids;
  }, [birdsEyeMode, birdsSelectedSet, canvasModel, localSelectedId, selectedObjectIds]);

  const memberHighlightIds = useMemo(() => {
    if (!session || !cttqMemberPickTargetId) return new Set<string>();
    return new Set(listTtqBirdsEyeConvergeMemberIds(session, cttqMemberPickTargetId));
  }, [cttqMemberPickTargetId, session]);

  const entryById = useMemo(() => {
    const map = new Map((session?.particles ?? []).map((entry) => [entry.particleId, entry] as const));
    return map;
  }, [session?.particles]);

  const resolveNodeAnchor = useCallback((particleId: string) => {
    const placed = numberNodePlacementById.get(particleId);
    if (placed) {
      return {
        cx: placed.x + placed.width / 2,
        cy: placed.y + placed.height / 2,
        // 左下方：避开编号小卡，留出风筝线
        left: placed.x - FLOAT_CARD_WIDTH - 40,
        top: placed.y + placed.height + 16,
      };
    }
    const rect = cardRectById.get(particleId);
    if (!rect) return null;
    return {
      cx: rect.x + rect.width / 2,
      cy: rect.y + rect.height / 2,
      left: rect.x - FLOAT_CARD_WIDTH - 40,
      top: rect.y + rect.height + 16,
    };
  }, [cardRectById, numberNodePlacementById]);

  const openFloatFor = useCallback((particleId: string, pin = false) => {
    if (!birdsEyeMode || convergeRectById.has(particleId)) return;
    const anchor = resolveNodeAnchor(particleId);
    if (!anchor || !entryById.has(particleId)) return;
    setFloats((current) => {
      const existing = current.find((item) => item.particleId === particleId);
      if (existing) {
        return current.map((item) => (
          item.particleId === particleId
            ? {
              ...item,
              pinned: pin ? true : item.pinned,
              nodeCx: anchor.cx,
              nodeCy: anchor.cy,
              left: item.pinned || pin ? item.left : anchor.left,
              top: item.pinned || pin ? item.top : anchor.top,
            }
            : item
        ));
      }
      return [...current, {
        particleId,
        pinned: pin,
        left: anchor.left,
        top: anchor.top,
        width: FLOAT_CARD_WIDTH,
        height: FLOAT_DEFAULT_H,
        nodeCx: anchor.cx,
        nodeCy: anchor.cy,
      }];
    });
  }, [birdsEyeMode, convergeRectById, entryById, resolveNodeAnchor]);

  const scheduleCloseFloat = useCallback((particleId: string) => {
    if (floatDragRef.current?.particleId === particleId) return;
    clearHoverTimer();
    hoverTimer.current = window.setTimeout(() => {
      const el = floatElsRef.current[particleId];
      if (el?.matches(':hover') || el?.querySelector(':hover')) return;
      setFloats((current) => current.filter((item) => (
        item.particleId !== particleId || item.pinned
      )));
    }, 320);
  }, [clearHoverTimer]);

  const closeFloat = useCallback((particleId: string) => {
    clearHoverTimer();
    setFloats((current) => current.filter((item) => item.particleId !== particleId));
  }, [clearHoverTimer]);

  const setFloatPinned = useCallback((particleId: string, pinned: boolean) => {
    clearHoverTimer();
    setFloats((current) => current.map((item) => (
      item.particleId === particleId ? { ...item, pinned } : item
    )));
  }, [clearHoverTimer]);

  const beginFloatDrag = useCallback((
    particleId: string,
    clientX: number,
    clientY: number,
    pointerId: number,
  ) => {
    const current = floats.find((item) => item.particleId === particleId);
    if (!current) return;
    clearHoverTimer();
    panRef.current = null;
    floatDragRef.current = {
      particleId,
      startClientX: clientX,
      startClientY: clientY,
      originLeft: current.left,
      originTop: current.top,
    };
    setFloats((items) => items.map((item) => (
      item.particleId === particleId ? { ...item, pinned: true } : item
    )));
    try {
      containerRef.current?.setPointerCapture(pointerId);
    } catch {
      // ignore
    }
  }, [clearHoverTimer, floats]);

  const handleNumberHoverStart = useCallback((particleId: string) => {
    clearHoverTimer();
    openFloatFor(particleId);
  }, [clearHoverTimer, openFloatFor]);

  const handleNumberHoverEnd = useCallback((particleId: string, relatedTarget: EventTarget | null) => {
    const related = relatedTarget as HTMLElement | null;
    if (related?.closest?.('[data-ttq-lod-float], [data-ttq-number-node]')) return;
    scheduleCloseFloat(particleId);
  }, [scheduleCloseFloat]);

  // 从 ttqibState.birdsEyePinnedFloats 恢复钉住浮窗（进关系层 / 重新加载品题）
  useEffect(() => {
    if (!birdsEyeMode) return;
    if (!cardRectById.size) return;
    setFloats((current) => {
      const byId = new Map(current.map((item) => [item.particleId, item]));
      let changed = false;
      for (const saved of pinnedFloats) {
        const anchor = resolveNodeAnchor(saved.particleId);
        if (!anchor || convergeRectById.has(saved.particleId)) continue;
        if (!entryById.has(saved.particleId)) continue;
        const existing = byId.get(saved.particleId);
        if (existing?.pinned) {
          if (existing.nodeCx !== anchor.cx || existing.nodeCy !== anchor.cy) {
            byId.set(saved.particleId, {
              ...existing,
              nodeCx: anchor.cx,
              nodeCy: anchor.cy,
            });
            changed = true;
          }
          continue;
        }
        byId.set(saved.particleId, {
          particleId: saved.particleId,
          pinned: true,
          left: Number.isFinite(saved.left) ? saved.left : anchor.left,
          top: Number.isFinite(saved.top) ? saved.top : anchor.top,
          width: existing?.width ?? FLOAT_CARD_WIDTH,
          height: existing?.height ?? FLOAT_DEFAULT_H,
          nodeCx: anchor.cx,
          nodeCy: anchor.cy,
        });
        changed = true;
      }
      return changed ? [...byId.values()] : current;
    });
    pinnedSnapshotRef.current = JSON.stringify(
      [...pinnedFloats]
        .map((item) => ({
          particleId: item.particleId,
          left: item.left,
          top: item.top,
        }))
        .sort((a, b) => a.particleId.localeCompare(b.particleId)),
    );
    readyToPersistPinsRef.current = true;
  }, [birdsEyeMode, cardRectById, convergeRectById, entryById, pinnedFloats, resolveNodeAnchor]);

  useEffect(() => {
    if (!onPinnedFloatsChange || !readyToPersistPinsRef.current) return;
    const next = floats
      .filter((item) => item.pinned)
      .map((item) => ({
        particleId: item.particleId,
        left: item.left,
        top: item.top,
      }))
      .sort((a, b) => a.particleId.localeCompare(b.particleId));
    const key = JSON.stringify(next);
    if (key === pinnedSnapshotRef.current) return;
    pinnedSnapshotRef.current = key;
    onPinnedFloatsChange(next);
  }, [floats, onPinnedFloatsChange]);

  useTtqCanvasMultiTouchPan({
    containerRef,
    vpRef,
    onViewportChange,
    onMultiPanStart: () => {
      panRef.current = null;
      floatDragRef.current = null;
      onClearSegmentTray?.();
      onBehaviorSelectionChange?.([]);
      onSelectObject?.(null);
      setLocalSelectedId(null);
      if (birdsEyeMode) onBirdsSelectedChange?.([]);
    },
    enabled: Boolean(canvasModel && onViewportChange),
  });

  const capture = useCallback((pointerId: number) => {
    try {
      containerRef.current?.setPointerCapture(pointerId);
    } catch {
      // ignore
    }
  }, []);

  const handleWheel = useCallback((event: React.WheelEvent) => {
    event.preventDefault();
    if (!event.ctrlKey || !onViewportChange) return;
    const container = containerRef.current;
    if (!container) return;
    const current = vpRef.current;
    const rect = container.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;
    const factor = Math.exp(-event.deltaY * 0.01);
    const nextZoom = Math.min(TTQIB_ZOOM_MAX, Math.max(TTQIB_ZOOM_MIN, current.zoom * factor));
    const worldX = (pointerX - current.x) / current.zoom;
    const worldY = (pointerY - current.y) / current.zoom;
    onViewportChange({
      x: pointerX - worldX * nextZoom,
      y: pointerY - worldY * nextZoom,
      zoom: nextZoom,
    });
  }, [onViewportChange]);

  useEffect(() => {
    if (!canvasModel) return;
    const container = containerRef.current;
    if (!container) return;
    const blockPageZoom = (event: WheelEvent) => {
      if (event.ctrlKey) event.preventDefault();
    };
    container.addEventListener('wheel', blockPageZoom, { passive: false });
    return () => container.removeEventListener('wheel', blockPageZoom);
  }, [canvasModel]);

  const handlePointerMove = useCallback((event: React.PointerEvent) => {
    const floatDrag = floatDragRef.current;
    if (floatDrag) {
      const zoom = Math.max(vpRef.current.zoom, 0.01);
      const dx = (event.clientX - floatDrag.startClientX) / zoom;
      const dy = (event.clientY - floatDrag.startClientY) / zoom;
      setFloats((current) => current.map((item) => (
        item.particleId === floatDrag.particleId
          ? {
            ...item,
            left: floatDrag.originLeft + dx,
            top: floatDrag.originTop + dy,
            pinned: true,
          }
          : item
      )));
      return;
    }
    const current = vpRef.current;
    if (panRef.current && onViewportChange) {
      const pan = panRef.current;
      const dx = event.clientX - pan.startClientX;
      const dy = event.clientY - pan.startClientY;
      if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) pan.moved = true;
      if (pan.moved) {
        onViewportChange({
          ...current,
          x: pan.originX + dx,
          y: pan.originY + dy,
        });
      }
    }
  }, [onViewportChange]);

  const birdsSelectedIdsRef = useRef(birdsSelectedIds);
  birdsSelectedIdsRef.current = birdsSelectedIds;

  const toggleBirdsSelection = useCallback((particleId: string) => {
    if (!onBirdsSelectedChange) return;
    const current = birdsSelectedIdsRef.current;
    const next = current.includes(particleId)
      ? current.filter((id) => id !== particleId)
      : [...current, particleId];
    onBirdsSelectedChange(next);
  }, [onBirdsSelectedChange]);

  const handleParticleActivate = useCallback((particleId: string) => {
    if (cttqMemberPickTargetId) {
      if (memberHighlightIds.has(particleId) || convergeRectById.has(particleId)) return;
      onPickCttqMember?.(cttqMemberPickTargetId, particleId);
      return;
    }
    if (birdsEyeMode) {
      toggleBirdsSelection(particleId);
      return;
    }
    const card = cards.find((item) => item.particleId === particleId);
    if (card) {
      setLocalSelectedId(card.whole.id);
      onSelectObject?.(card.whole.id);
      onBehaviorSelectionChange?.([particleId]);
    }
  }, [
    birdsEyeMode,
    cards,
    convergeRectById,
    cttqMemberPickTargetId,
    memberHighlightIds,
    onBehaviorSelectionChange,
    onPickCttqMember,
    onSelectObject,
    toggleBirdsSelection,
  ]);

  const endInteractions = useCallback((event: React.PointerEvent) => {
    if (floatDragRef.current) {
      floatDragRef.current = null;
      try {
        containerRef.current?.releasePointerCapture(event.pointerId);
      } catch {
        // ignore
      }
      return;
    }
    const pan = panRef.current;
    if (pan) {
      if (!pan.moved && event.button === 0 && !pan.fromCard) {
        if (cttqMemberPickTargetId) {
          onCancelCttqMemberPick?.();
        } else if (birdsEyeMode) {
          // 空白点击只收起未钉浮窗，保留多选，便于继续点选构造收敛
          setFloats((current) => current.filter((item) => item.pinned));
        } else {
          setLocalSelectedId(null);
          onSelectObject?.(null);
          onBehaviorSelectionChange?.([]);
          onClearSegmentTray?.();
        }
      }
      panRef.current = null;
    }

    try {
      containerRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  }, [
    birdsEyeMode,
    cttqMemberPickTargetId,
    onBehaviorSelectionChange,
    onCancelCttqMemberPick,
    onClearSegmentTray,
    onSelectObject,
  ]);

  const beginPan = useCallback((
    clientX: number,
    clientY: number,
    pointerId: number,
    fromCard: boolean,
  ) => {
    const current = vpRef.current;
    panRef.current = {
      startClientX: clientX,
      startClientY: clientY,
      originX: current.x,
      originY: current.y,
      moved: false,
      fromCard,
    };
    capture(pointerId);
  }, [capture]);

  const handleBackgroundPointerDown = useCallback((event: React.PointerEvent) => {
    if (event.button !== 0 && event.button !== 1) return;
    if ((event.target as HTMLElement).closest('[data-ttq-particle], [data-ttq-lod-float]')) return;
    beginPan(event.clientX, event.clientY, event.pointerId, false);
  }, [beginPan]);

  const handleCardPanStart = useCallback((
    _particleId: string,
    clientX: number,
    clientY: number,
    pointerId: number,
  ) => {
    beginPan(clientX, clientY, pointerId, true);
  }, [beginPan]);

  const handleSelectWhole = useCallback((objectId: string) => {
    if (birdsEyeMode) {
      const object = canvasModel?.objects.find((item) => item.id === objectId);
      if (object?.particleId) handleParticleActivate(object.particleId);
      return;
    }
    setLocalSelectedId(objectId);
    onSelectObject?.(objectId);
  }, [birdsEyeMode, canvasModel?.objects, handleParticleActivate, onSelectObject]);

  const handleToggleSegment = useCallback((item: { objectId: string; text: string; particleId: string }) => {
    // 鸟瞰收敛卡也需要选段 → AI 对话；勿吞成整卡多选
    setLocalSelectedId(item.objectId);
    onSelectObject?.(item.objectId);
    onToggleSegment?.(item);
  }, [onSelectObject, onToggleSegment]);

  const handleSelectImage = useCallback((objectId: string) => {
    if (birdsEyeMode) {
      const object = canvasModel?.objects.find((item) => item.id === objectId);
      if (object?.particleId) handleParticleActivate(object.particleId);
      return;
    }
    setLocalSelectedId(objectId);
    onSelectObject?.(objectId);
  }, [birdsEyeMode, canvasModel?.objects, handleParticleActivate, onSelectObject]);

  const handleOpenPinTi = useCallback((particleId: string) => {
    if (birdsEyeMode) {
      handleParticleActivate(particleId);
      return;
    }
    onOpenPinTi?.(particleId);
  }, [birdsEyeMode, handleParticleActivate, onOpenPinTi]);

  const centerCardInView = useCallback((particleId: string): boolean => {
    const container = containerRef.current;
    const rect = resolveRect(particleId);
    if (!container || !rect || !onViewportChange) return false;
    const box = container.getBoundingClientRect();
    onViewportChange(computeFocusCardViewportInLod(
      rect,
      box.width,
      box.height,
      { birdsEyeMode, currentZoom: vp.zoom },
    ));
    return true;
  }, [birdsEyeMode, onViewportChange, resolveRect, vp.zoom]);

  useEffect(() => {
    if (!focusParticleId) return;
    // 布局异步重建时目标可能尚未进画布；成功定位后再 clear，避免一次性机会被吃掉
    if (!centerCardInView(focusParticleId)) return;
    onFocusParticleHandled?.();
  }, [centerCardInView, focusParticleId, onFocusParticleHandled]);

  useEffect(() => {
    if (!cttqMemberPickTargetId) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancelCttqMemberPick?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cttqMemberPickTargetId, onCancelCttqMemberPick]);

  const zoomBy = useCallback((factor: number) => {
    if (!onViewportChange || !containerRef.current) return;
    const current = vpRef.current;
    const rect = containerRef.current.getBoundingClientRect();
    const pointerX = rect.width / 2;
    const pointerY = rect.height / 2;
    const nextZoom = Math.min(TTQIB_ZOOM_MAX, Math.max(TTQIB_ZOOM_MIN, current.zoom * factor));
    const worldX = (pointerX - current.x) / current.zoom;
    const worldY = (pointerY - current.y) / current.zoom;
    onViewportChange({
      x: pointerX - worldX * nextZoom,
      y: pointerY - worldY * nextZoom,
      zoom: nextZoom,
    });
  }, [onViewportChange]);

  const fitView = useCallback(() => {
    const container = containerRef.current;
    if (!container || !canvasModel || !onViewportChange) return;
    const rect = container.getBoundingClientRect();
    const wholes = canvasModel.objects.filter((item) => item.type === 'particle-whole');
    onViewportChange(computeFitViewport(wholes, rect.width, Math.max(rect.height, 400)));
  }, [canvasModel, onViewportChange]);

  useEffect(() => {
    if (!canvasModel?.objects.length || !onViewportChange || viewportInitialized) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const wholes = canvasModel.objects.filter((item) => item.type === 'particle-whole');
    onViewportChange(computeDefaultColumnViewport(
      wholes.length ? wholes : canvasModel.objects,
      rect.width || 1200,
      Math.max(rect.height, 400),
      2.5,
    ));
  }, [canvasModel, onViewportChange, viewportInitialized]);

  if (!canvasModel) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#fafafa] text-sm text-muted">
        正在加载画布…
      </div>
    );
  }

  const showDeriveFull = !birdsEyeMode;
  const edgeOpacityClass = 'transition-opacity duration-200';

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#f3f1ec]">
      <div className={`pointer-events-none absolute right-4 z-20 flex gap-1 ${TTQ_SESSION_OVERLAY_TOP_CLASS}`}>
        <button
          type="button"
          className="pointer-events-auto inline-flex items-center justify-center rounded-lg border border-border bg-surface/95 px-2 py-1 text-xs shadow-sm"
          title="适配"
          aria-label="适配"
          onClick={fitView}
        >
          <Scan size={14} />
        </button>
        <button
          type="button"
          className="pointer-events-auto rounded-lg border border-border bg-surface/95 px-2 py-1 text-xs shadow-sm"
          title="放大"
          aria-label="放大"
          onClick={() => zoomBy(1.12)}
        >
          ＋
        </button>
        <button
          type="button"
          className="pointer-events-auto rounded-lg border border-border bg-surface/95 px-2 py-1 text-xs shadow-sm"
          title="缩小"
          aria-label="缩小"
          onClick={() => zoomBy(0.89)}
        >
          －
        </button>
        <span className="rounded-lg border border-border bg-surface/95 px-2 py-1 text-xs text-muted shadow-sm">
          {Math.round(vp.zoom * 100)}%
        </span>
      </div>

      {cttqMemberPickTargetId ? (
        <div className={`pointer-events-none absolute inset-x-0 z-30 flex justify-center px-4 ${TTQ_SESSION_OVERLAY_TOP_CLASS}`}>
          <div className="pointer-events-auto flex items-center gap-3 rounded-xl border border-teal-300/80 bg-teal-50/95 px-3 py-2 text-xs text-teal-900 shadow-md">
            <span>点击画布上的节点加入当前聚合（已高亮成员不可重复选）· Esc 取消</span>
            <button
              type="button"
              className="rounded-md border border-teal-400 bg-white px-2 py-0.5"
              onClick={() => onCancelCttqMemberPick?.()}
            >
              取消
            </button>
          </div>
        </div>
      ) : null}

      <div
        ref={containerRef}
        className="relative h-full w-full touch-none cursor-grab active:cursor-grabbing overflow-hidden"
        onWheel={handleWheel}
        onPointerDown={handleBackgroundPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endInteractions}
        onPointerCancel={endInteractions}
        onContextMenu={(event) => event.preventDefault()}
      >
        <div
          className="absolute left-0 top-0"
          style={{
            transform: `translate(${vp.x}px, ${vp.y}px) scale(${vp.zoom})`,
            transformOrigin: '0 0',
            willChange: 'transform',
          }}
        >
          {cards.length === 0 ? (
            <div className="absolute left-24 top-24 rounded-xl border border-dashed border-stone-300 bg-white/80 px-6 py-4 text-sm text-muted shadow-sm">
              暂无知识颗粒。添加颗粒后将直接铺在画布上。
            </div>
          ) : null}

          <svg
            className={`pointer-events-none absolute left-0 top-0 z-0 overflow-visible ${edgeOpacityClass}`}
            width={worldSize.width}
            height={worldSize.height}
          >
            {deriveEdges.map((edge) => {
              const from = resolveRect(edge.fromId);
              const to = resolveRect(edge.toId);
              if (!from || !to) return null;
              const edgeActive = selectedParticleIdSet.has(edge.fromId)
                || selectedParticleIdSet.has(edge.toId);
              return (
                <line
                  key={edge.id}
                  x1={from.x + from.width / 2}
                  y1={from.y + from.height / 2}
                  x2={to.x + to.width / 2}
                  y2={to.y}
                  stroke={edgeActive ? 'rgba(79, 70, 229, 0.85)' : 'rgba(79, 70, 229, 0.45)'}
                  strokeWidth={edgeActive ? 3.5 : 3}
                  strokeDasharray={edgeActive ? undefined : '5 4'}
                  opacity={showDeriveFull ? 1 : 0.35}
                />
              );
            })}
            {convergeEdges.map((edge) => {
              const from = resolveRect(edge.fromId);
              const to = resolveRect(edge.toId);
              if (!from || !to) return null;
              const edgeActive = selectedParticleIdSet.has(edge.fromId)
                || selectedParticleIdSet.has(edge.toId);
              const x1 = from.x + from.width / 2;
              const y1 = from.y + from.height / 2;
              const x2 = to.x + to.width / 2;
              const y2 = to.y;
              const exit = birdsEyeMode
                ? ttqRayAabbExitPoint(from, x1, y1, x2, y2)
                : null;
              const dotR = Math.max(9, Math.min(14, from.width * 0.055));
              return (
                <g key={edge.id}>
                  <line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={edgeActive ? 'rgba(13, 148, 136, 0.9)' : 'rgba(13, 148, 136, 0.55)'}
                    strokeWidth={edgeActive ? 4 : 3.25}
                    strokeDasharray="4 3"
                    opacity={1}
                  />
                  {exit ? (
                    <circle
                      cx={exit.x}
                      cy={exit.y}
                      r={dotR}
                      fill="#ef4444"
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  ) : null}
                </g>
              );
            })}
            {birdsEyeMode
              ? floats.map((floatItem) => {
                const visualW = (floatItem.width || FLOAT_CARD_WIDTH) * convergeScreenBoost;
                return (
                  <line
                    key={`kite:${floatItem.particleId}`}
                    x1={floatItem.nodeCx}
                    y1={floatItem.nodeCy}
                    x2={floatItem.left + visualW / 2}
                    y2={floatItem.top}
                    stroke="rgba(120,113,108,0.55)"
                    strokeWidth={2.25}
                  />
                );
              })
              : null}
          </svg>

          {/* 状态 B：一对多整卡 */}
          {showDeriveFull
            ? cards.map((card) => (
              <TtqParticleCard
                key={card.particleId}
                card={card}
                selectedObjectId={localSelectedId}
                selectedSegmentObjectIds={selectedSegmentObjectIds}
                onSelectWhole={handleSelectWhole}
                onToggleSegment={handleToggleSegment}
                onSelectImage={handleSelectImage}
                onOpenPinTi={handleOpenPinTi}
                onOpenLadderPractice={onOpenLadderPractice}
                showLadderPractice={showLadderPractice}
                onCenterInView={centerCardInView}
                onCanvasPanStart={handleCardPanStart}
                tastedAnchorTexts={tastedAnchorsByParticleId[card.particleId] || []}
                chatAnchors={chatAnchorsByParticleId[card.particleId] || []}
                onOpenChatThread={onOpenChatThread}
                isDerived={derivedIdSet.has(card.particleId) || Boolean(derivedProvenanceByParticleId[card.particleId])}
                provenance={derivedProvenanceByParticleId[card.particleId] || null}
                displayNumber={displayNumbers[card.particleId]}
                onDeleteDerived={onDeleteDerivedParticle}
                onRegenerateDerived={onRegenerateDerivedParticle}
                regeneratingDerivedId={regeneratingDerivedId}
                onMeasureHeight={onMeasureParticle}
                linkedRefs={linkedRefsByParticleId[card.particleId] || []}
                onFocusLinked={onFocusLinkedParticle}
              />
            ))
            : null}

          {/* 状态 A：衍生/原始 → 标号节点（避让后落位；逻辑尺寸已含 boost） */}
          {!showDeriveFull
            ? cards
              .filter((card) => !convergeIdSet.has(card.particleId))
              .map((card) => {
                const placed = numberNodePlacementById.get(card.particleId);
                if (!placed) return null;
                const label = displayNumbers[card.particleId] || '·';
                return (
                  <TtqNumberNode
                    key={`num:${card.particleId}`}
                    particleId={card.particleId}
                    x={placed.x}
                    y={placed.y}
                    width={placed.width}
                    height={placed.height}
                    label={label}
                    title={card.title}
                    selected={birdsSelectedSet.has(card.particleId)}
                    memberLinked={memberHighlightIds.has(card.particleId)}
                    pickMode={Boolean(cttqMemberPickTargetId)}
                    derived={derivedIdSet.has(card.particleId) || Boolean(derivedProvenanceByParticleId[card.particleId])}
                    onPointerDown={handleCardPanStart}
                    onActivate={handleParticleActivate}
                    onHoverStart={handleNumberHoverStart}
                    onHoverEnd={handleNumberHoverEnd}
                  />
                );
              })
            : null}

          {/* hover / 钉住浮窗：与收敛卡同宽、同组件风格、同屏幕补偿（非一对多整卡 preview） */}
          {birdsEyeMode
            ? floats.map((floatItem) => {
              const entry = entryById.get(floatItem.particleId);
              if (!entry) return null;
              const card = buildParticleCardFromEntry(entry, {
                x: 0,
                y: 0,
                width: TTQ_CONVERGE_CARD_W,
                layerId: 'lod-float',
              });
              const derived = derivedIdSet.has(floatItem.particleId)
                || Boolean(derivedProvenanceByParticleId[floatItem.particleId])
                || (session ? isQuestionChainDerivedParticle(session, floatItem.particleId) : false);
              const provenance = session
                ? resolveDerivedProvenance(session, floatItem.particleId)
                : (derivedProvenanceByParticleId[floatItem.particleId] || null);
              return (
                <div
                  key={`float:${floatItem.particleId}`}
                  ref={(node) => {
                    floatElsRef.current[floatItem.particleId] = node;
                  }}
                  data-ttq-lod-float=""
                  className="absolute z-40 touch-none"
                  style={{
                    left: floatItem.left,
                    top: floatItem.top,
                    width: TTQ_CONVERGE_CARD_W,
                    transform: convergeScreenBoost !== 1 ? `scale(${convergeScreenBoost})` : undefined,
                    transformOrigin: 'top left',
                  }}
                  onMouseEnter={clearHoverTimer}
                  onMouseLeave={(event) => {
                    const related = event.relatedTarget as HTMLElement | null;
                    if (related?.closest?.('[data-ttq-lod-float], [data-ttq-lod-float-chrome], [data-ttq-number-node]')) {
                      return;
                    }
                    scheduleCloseFloat(floatItem.particleId);
                  }}
                >
                  <div
                    className="absolute -left-2 -top-2 z-[60] flex pointer-events-auto gap-1"
                    data-ttq-lod-float-chrome=""
                  >
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 cursor-grab items-center justify-center rounded-full border border-border bg-surface text-muted shadow-sm active:cursor-grabbing hover:bg-black/[0.04] hover:text-ink"
                      title="拖动浮窗"
                      aria-label="拖动浮窗"
                      onPointerDown={(event) => {
                        if (event.button !== 0 && event.button !== 1) return;
                        event.stopPropagation();
                        event.preventDefault();
                        clearHoverTimer();
                        beginFloatDrag(
                          floatItem.particleId,
                          event.clientX,
                          event.clientY,
                          event.pointerId,
                        );
                      }}
                      onMouseEnter={clearHoverTimer}
                    >
                      <GripVertical size={14} />
                    </button>
                    <button
                      type="button"
                      className={
                        'inline-flex h-8 w-8 items-center justify-center rounded-full border shadow-sm '
                        + (floatItem.pinned
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-surface text-muted hover:bg-black/[0.04] hover:text-ink')
                      }
                      title={floatItem.pinned ? '取消钉住' : '钉住浮窗'}
                      aria-pressed={floatItem.pinned}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        clearHoverTimer();
                      }}
                      onMouseEnter={clearHoverTimer}
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        clearHoverTimer();
                        setFloatPinned(floatItem.particleId, !floatItem.pinned);
                      }}
                    >
                      {floatItem.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-surface text-muted shadow-sm hover:bg-black/[0.04] hover:text-ink"
                      title="关闭浮窗"
                      aria-label="关闭浮窗"
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        clearHoverTimer();
                      }}
                      onMouseEnter={clearHoverTimer}
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        closeFloat(floatItem.particleId);
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <TtqParticleCard
                    card={card}
                    selectedObjectId={null}
                    isDerived={derived}
                    provenance={provenance}
                    displayNumber={displayNumbers[floatItem.particleId]}
                    onCenterInView={centerCardInView}
                    onCanvasPanStart={(particleId, clientX, clientY, pointerId) => {
                      beginFloatDrag(particleId, clientX, clientY, pointerId);
                    }}
                    onDeleteDerived={onDeleteDerivedParticle}
                    onRegenerateDerived={onRegenerateDerivedParticle}
                    regeneratingDerivedId={regeneratingDerivedId}
                    cttqAggregating={cttqAggregating}
                  />
                </div>
              );
            })
            : null}

          {/* 收敛完整卡：同 TtqParticleCard；关系层做屏幕尺寸补偿，避免缩得看不清 */}
          {convergePlacements.map((item) => {
            const card = convergeBoardCards.get(item.particleId);
            if (!card) return null;
            const label = displayNumbers[item.particleId] || '·';
            const provenance = session
              ? resolveDerivedProvenance(session, item.particleId)
              : (derivedProvenanceByParticleId[item.particleId] || null);
            const selected = birdsSelectedSet.has(item.particleId);
            return (
              <div
                key={`cvg:${item.particleId}`}
                data-ttq-converge-card=""
                className="absolute z-20"
                style={{
                  left: item.x,
                  top: item.y,
                  width: item.width,
                  transform: convergeScreenBoost !== 1 ? `scale(${convergeScreenBoost})` : undefined,
                  transformOrigin: 'top left',
                  pointerEvents: cttqMemberPickTargetId ? 'none' : undefined,
                }}
              >
                <TtqParticleCard
                  card={card}
                  selectedObjectId={selected ? card.whole.id : null}
                  selectedSegmentObjectIds={selectedSegmentObjectIds}
                  isDerived
                  provenance={provenance}
                  displayNumber={label}
                  onSelectWhole={() => {
                    if (birdsEyeMode) {
                      handleParticleActivate(item.particleId);
                      return;
                    }
                    onEnterBirdsEyeZoom?.();
                    onBirdsSelectedChange?.([item.particleId]);
                  }}
                  onToggleSegment={handleToggleSegment}
                  onOpenChatThread={onOpenChatThread}
                  chatAnchors={chatAnchorsByParticleId[item.particleId] || []}
                  onCenterInView={centerCardInView}
                  onCanvasPanStart={handleCardPanStart}
                  onDeleteDerived={onDeleteDerivedParticle}
                  onRegenerateDerived={onRegenerateDerivedParticle}
                  onAddCttqMember={onStartCttqMemberPick}
                  regeneratingDerivedId={regeneratingDerivedId}
                  cttqAggregating={cttqAggregating}
                  onMeasureHeight={(particleId, size) => {
                    setConvergeMeasuredH((current) => (
                      current[particleId] === size.height
                        ? current
                        : { ...current, [particleId]: size.height }
                    ));
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export type { TtqNativeCanvasProps };
